import type { AuditPlanDetailDTO } from "@/modules/internal-audit/domain/contracts";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";

interface AuditPlanPdfPreviewContentProps {
  auditPlan: AuditPlanDetailDTO;
}

const statusLabels: Record<string, string> = {
  pending: "ຍັງບໍ່ກວດສອບ",
  in_progress: "ກຳລັງກວດສອບ",
  completed: "ກວດສອບແລ້ວ",
  cancelled: "ຍົກເລີກ",
};

const frequencyLabels: Record<string, string> = {
  monthly: "ປະຈຳເດືອນ",
  quarterly: "ປະຈຳໄຕມາດ",
  semi_annual: "ຄັ້ງຕໍ່ 6 ເດືອນ",
  annual: "ປະຈຳປີ",
  ad_hoc: "ຕາມຄວາມຈຳເປັນ",
};

export function AuditPlanPdfPreviewContent({
  auditPlan,
}: AuditPlanPdfPreviewContentProps) {
  return (
    <div
      id="audit-plan-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header - Letterhead Style */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">ຜະແນການກວດສອບພາຍໃນ</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ເອກະສານເລກທີ:{" "}
              <span className="font-normal">{`Plan-${auditPlan?.id.slice(-6).toUpperCase()}`}</span>
            </p>
            <p className="mt-1 font-semibold">
              ຊື່ຜະແນການກວດສອບ:{" "}
              <span className="font-normal">
                {auditPlan?.name || "ບໍ່ໄດ້ກໍານົດ"}
              </span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* Plan Title and Status */}
      <div className="mb-12 text-center">
        <h2 className="mb-6 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          ຜະແນການກວດສອບພາຍໃນ
        </h2>
        <p className="font-semibold text-gray-700 text-xl">
          ສະຖານະ: {statusLabels[auditPlan?.status] || auditPlan?.status || "-"}
          {!auditPlan?.isActive && " | ບໍ່ໃຊ້ງານ"}
        </p>
      </div>

      {/* Section 1: ข้อมูลพื้นฐาน */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          1. ຂໍ້ມູນພື້ນຖານ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຊື່ຜະແນການກວດສອບ
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {auditPlan?.name || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            {auditPlan?.description && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ລາຍລະອຽດ
                </td>
                <td className="whitespace-pre-wrap p-4 text-sm leading-relaxed">
                  {auditPlan.description}
                </td>
              </tr>
            )}
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ປະເພດການກວດສອບ
              </td>
              <td className="p-4 text-sm">
                {auditPlan?.auditTypeName || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ພະແນກ
              </td>
              <td className="p-4 text-sm">
                {auditPlan?.departmentName || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຕໍາແໜ່ງຜູ້ກວດສອບ
              </td>
              <td className="p-4 text-sm">
                {auditPlan?.auditorPositionTitle || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຄວາມຖີ່
              </td>
              <td className="p-4 text-sm">
                {frequencyLabels[auditPlan?.frequency] ||
                  auditPlan?.frequency ||
                  "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີກວດສອບຄັ້ງຖັດ
              </td>
              <td className="p-4 text-sm">
                {auditPlan?.nextAuditDate
                  ? formatDateLocal(auditPlan.nextAuditDate)
                  : "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ສະຖານະ
              </td>
              <td className="p-4 font-semibold text-sm">
                {statusLabels[auditPlan?.status] || auditPlan?.status || "-"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Section 2: Related Items */}
      {(auditPlan?.sopName || auditPlan?.qcStandardName) && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            2. ລາຍການທີ່ກ່ຽວຂ້ອງ
          </h3>

          <table className="mb-6 w-full border-collapse border-2 border-gray-400">
            <tbody>
              {auditPlan.sopName && (
                <tr className="border-gray-300 border-b">
                  <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                    SOP
                  </td>
                  <td className="p-4 text-sm leading-relaxed">
                    {auditPlan.sopName}
                  </td>
                </tr>
              )}
              {auditPlan.qcStandardName && (
                <tr>
                  <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                    ມາດຕະຖານ QC
                  </td>
                  <td className="p-4 text-sm leading-relaxed">
                    {auditPlan.qcStandardName}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Section 3: Metadata */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          3. ຂໍ້ມູນເພີ່ມເຕີມ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ສ້າງເມື່ອ
              </td>
              <td className="p-4 text-sm">
                {auditPlan?.createdAt
                  ? formatDateTimeLocal(auditPlan.createdAt)
                  : "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ອັບເດດຄັ້ງລ່າສຸດ
              </td>
              <td className="p-4 text-sm">
                {auditPlan?.updatedAt
                  ? formatDateTimeLocal(auditPlan.updatedAt)
                  : "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="mt-12 border-gray-300 border-t-2 pt-6 text-center text-gray-600 text-sm">
        <p>ເອກະສານນີ້ຖືກສ້າງອັດຕະໂນມັດໂດຍລະບົບ Freedom ERP</p>
        <p className="mt-1">
          ວັນທີພິມ: {formatDateTimeLocal(new Date())} | ເລກທີແຜນ: {auditPlan?.id}
        </p>
      </div>
    </div>
  );
}
